import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Panel, can } from "@/components/staff/Panel";
import { PageHeader } from "@/components/common";
import TaskTable from "@/components/TaskTable";
import TaskDrawer from "@/components/TaskDrawer";
import UsersPanel from "@/components/UsersPanel";
import Tasks from "@/pages/Tasks";
import CalendarPage from "@/pages/CalendarPage";
import Companies from "@/pages/Companies";
import Registrations from "@/pages/Registrations";
import Templates from "@/pages/Templates";
import Authorities from "@/pages/Authorities";
import Holidays from "@/pages/Holidays";
import Audit from "@/pages/Audit";
import Billing from "@/pages/Billing";
import Vault from "@/components/staff/Vault";
import ClientsSummary from "@/components/clients/Summary";
import { REG_DEFAULT } from "@/lib/regRange";

const SUMMARY_JOBS = ["va_admin", "compliance_officer", "finance_officer"];
import Roles from "@/components/staff/Roles";
import { Scorecards, Targets } from "@/components/staff/StaffPerf";
import Team from "@/components/perf/Team";
import Learning from "@/components/perf/Learning";
import { Approvals } from "@/pages/Queue";
import { api } from "@/lib/api";
import WaTemplates from "@/components/staff/WaTemplates";
import EmailTemplates from "@/components/staff/EmailTemplates";
import LeaveAdmin from "@/components/hr/LeaveAdmin";
import TargetsAdmin from "@/components/hr/TargetsAdmin";
import AttendanceAdmin from "@/components/hr/AttendanceAdmin";

function Unassigned() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(null);
  const [key, setKey] = useState(0);
  useEffect(() => { api.get("/tasks", { params: { handling: "va_assist", unassigned: true } }).then((r) => setTasks(r.data.filter((t) => !t.assignee_id && t.status !== "verified"))).catch(() => setTasks([])); }, [key]);
  return (
    <div className="space-y-8" data-testid="tasks-unassigned">
      <section><p className="mb-3 text-sm text-muted-foreground">Accepted work still waiting for a named handler. Open a task to assign it.</p><TaskTable tasks={tasks} onOpen={setOpen} /></section>
      <section data-testid="tasks-awaiting-approval"><h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Generated tasks awaiting approval</h3><Approvals onOpen={setOpen} refreshKey={key} /></section>
      <TaskDrawer taskId={open} onClose={() => setOpen(null)} onChanged={() => setKey((k) => k + 1)} />
    </div>
  );
}

export const TasksPanel = () => <Panel id="tasks" title="Tasks" tabs={[{ k: "list", l: "Tasks", el: <Tasks /> }, { k: "cal", l: "Calendar", el: <CalendarPage /> }, { k: "un", l: "Unassigned", el: <Unassigned /> }]} />;

export function ClientsPanel() {
  const { user } = useAuth();
  const [rf, setRf] = useState(REG_DEFAULT);
  const sum = user.role === "admin" || SUMMARY_JOBS.includes(user.perms?.job_role);
  return <Panel id="clients" title="Clients" tabs={[{ k: "sum", l: "Summary", show: sum, el: <ClientsSummary rf={rf} setRf={setRf} canReview={can(user, "signups_review")} /> },
    { k: "cos", l: "Companies", el: <Companies rf={rf} setRf={setRf} /> }, { k: "onb", l: "Sign-ups", show: can(user, "signups_review"), el: <Registrations rf={rf} setRf={setRf} /> },
    { k: "vault", l: "Vault", el: <Vault /> }]} />;
}

export function LibraryPanel() {
  const { user } = useAuth();
  return <Panel id="library" title="Library" tabs={[{ k: "tpl", l: "Templates", show: ["templates", "templates_archive", "templates_delete"].some((k) => can(user, k)), el: <Templates /> }, { k: "auth", l: "Authorities", show: can(user, "settings"), el: <Authorities /> }, { k: "hol", l: "Holidays", show: can(user, "holidays_manage"), el: <Holidays /> }]} />;
}

function StaffTab() {
  const { user } = useAuth();
  const [t, setT] = useState("acc");
  const tabs = [["acc", "Accounts", user.role === "admin"], ["role", "Role scorecards", can(user, "staff_perf")], ["learn", "Learning", can(user, "staff_perf")], ["cards", "Legacy scorecards", can(user, "staff_perf")], ["targets", "Legacy targets", can(user, "staff_perf")]].filter((x) => x[2]);
  const cur = tabs.find((x) => x[0] === t) ? t : tabs[0]?.[0];
  return <div className="ch chs phd"><div className="seg2 ptabs" style={{ margin: "0 0 14px" }} data-testid="staff-subtabs">{tabs.map(([k, l]) => <span key={k} className={cur === k ? "on" : ""} onClick={() => setT(k)} data-testid={`staff-sub-${k}`}>{l}</span>)}</div>
    {cur === "acc" && <><PageHeader title="Staff" subtitle="V.A staff accounts. Use Access to give each person a role category." /><UsersPanel scope="staff" /></>}{cur === "role" && <Team />}{cur === "learn" && <Learning />}{cur === "cards" && <><p className="note" data-testid="legacy-note">Legacy activity metrics, kept for continuity. They are not part of the role score and task status changes are not completions.</p><Scorecards /></>}{cur === "targets" && <Targets />}</div>;
}

export function AdminPanel() {
  const { user } = useAuth();
  return <Panel id="admin" title="Admin" tabs={[{ k: "users", l: "Users", show: can(user, "users_admin") || can(user, "roles_manage"), el: <>{can(user, "users_admin") && <><PageHeader title="Users" subtitle="Client accounts: invite, deactivate and reactivate. Audit history is always kept." /><UsersPanel scope="client" /></>}<Roles /></> },
    { k: "staff", l: "Staff", show: user.role === "admin" || can(user, "staff_perf"), el: <StaffTab /> },
    { k: "wa", l: "WhatsApp templates", show: can(user, "whatsapp_contact"), el: <WaTemplates /> },
    { k: "email", l: "Email templates", show: user?.role === "admin", el: <EmailTemplates /> },
    { k: "targets", l: "Staff targets", show: user.role === "admin", el: <TargetsAdmin /> },
    { k: "attendance", l: "Team attendance", show: user.role === "admin", el: <AttendanceAdmin /> },
    { k: "leave", l: "Leave requests", show: user.role === "admin", el: <LeaveAdmin /> },
    { k: "audit", l: "Audit", show: can(user, "audit"), el: <Audit /> }]} />;
}

export const PaymentsPanel = () => <Panel id="payments" title="Payments" tabs={[{ k: "all", l: "Payments", el: <Billing /> }]} />;
